import { Label, Card } from "@tellescope/design-system";
import { Button, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import type { ProfileEditBlock as ProfileEditBlockType } from "../../../shared/schema";
import { useProfileEdit } from "../../hooks/useProfileEdit";
import { useEnduserStore } from "../../store/hooks";
import { AvatarUpload } from "./AvatarUpload";
import { StateSelectField } from "@tellescope/forms";
import { ProfileFieldInput } from "./ProfileFieldInput";

interface ProfileEditBlockProps {
  block: ProfileEditBlockType;
}

export const ProfileEditBlock = ({ block }: ProfileEditBlockProps) => {
  const { t } = useTranslation("contacts");
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const { fields, isDirty, setValue, resetForm, submitForm, isSubmitting, error, successMessage, onAvatarUpload } =
    useProfileEdit({ block });

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    await submitForm();
  };

  const personalFields = fields.filter((f) => ["fname", "lname", "email", "phone"].includes(f.key));
  const addressFields = fields.filter((f) =>
    ["addressLineOne", "addressLineTwo", "city", "state", "zipCode"].includes(f.key)
  );

  return (
    <Card variant="floating" padding="root">
      {block.props.title && (
        <Card.Header className="pb-4">
          <Card.Title className="text-lg">{block.props.title}</Card.Title>
        </Card.Header>
      )}
      <Card.Body className={cn(!block.props.title && "pt-6")}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Avatar */}
          {currentEnduser && <AvatarUpload enduser={currentEnduser} onUpload={onAvatarUpload} />}

          {/* Personal information */}
          {personalFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {personalFields.map((field) => (
                <ProfileFieldInput key={field.key} field={field} onChange={(value) => setValue(field.key, value)} />
              ))}
            </div>
          )}

          {/* Address information */}
          {addressFields.length > 0 && (
            <div className="space-y-4">
              {/* Address lines - full width */}
              {addressFields.map((field) => {
                if (field.key !== "addressLineOne" && field.key !== "addressLineTwo") return null;
                return (
                  <ProfileFieldInput key={field.key} field={field} onChange={(value) => setValue(field.key, value)} />
                );
              })}

              {/* City, State, ZIP - responsive grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {addressFields.map((field) => {
                  if (field.key === "city") {
                    return (
                      <div key={field.key} className="col-span-2 sm:col-span-2">
                        <ProfileFieldInput field={field} onChange={(value) => setValue(field.key, value)} />
                      </div>
                    );
                  }
                  if (field.key === "state") {
                    return (
                      <div key={field.key} className="col-span-1 space-y-2">
                        <Label className="text-sm font-medium">
                          {field.label}
                          {field.required && <span className="text-destructive ml-1">*</span>}
                        </Label>
                        <StateSelectField value={field.value} onValueChange={(value) => setValue(field.key, value)} />
                      </div>
                    );
                  }
                  if (field.key === "zipCode") {
                    return (
                      <div key={field.key} className="col-span-1">
                        <ProfileFieldInput field={field} onChange={(value) => setValue(field.key, value)} />
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {/* Status messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && !error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-green-50 text-green-700 text-sm dark:bg-green-950 dark:text-green-300">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-2">
            {(block.props.showResetButton ?? true) && (
              <Button type="button" variant="outline" onClick={resetForm} disabled={isSubmitting || !isDirty}>
                {t("Reset")}
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {block.props.submitButtonText}
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
};
